"use client";

import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import styles from './MyProfileHeroSlider.module.scss';
import { slidersData } from './MyProfileHeroSlider.data';
import Text from '@components/atoms/Text';
import Button from '@components/atoms/Button';
import ImageResponsiveAssets from '@components/atoms/ImageResponsiveAssets';
import { PAGE_TYPES } from '@constants/pageTypes';

interface MyProfileHeroSliderProps {
  className?: string;
  cityName?: string;
}

const MyProfileHeroSlider: React.FC<MyProfileHeroSliderProps> = memo(({ className, cityName }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStartedAutoSlide, setHasStartedAutoSlide] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const resetAutoSlide = useCallback(() => {
    if (!hasStartedAutoSlide || !isVisible) return;
    
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev < slidersData.length - 1 ? prev + 1 : 0));
    }, 8000);
  }, [hasStartedAutoSlide, isVisible]);

  const goToSlide = useCallback((slideIndex: number) => {
    setCurrentSlide(slideIndex);
    resetAutoSlide();
  }, [resetAutoSlide]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev < slidersData.length - 1 ? prev + 1 : 0));
    resetAutoSlide();
  }, [resetAutoSlide]);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev > 0 ? prev - 1 : slidersData.length - 1));
    resetAutoSlide();
  }, [resetAutoSlide]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      goToNextSlide();
    } else if (distance < -minSwipeDistance) {
      goToPrevSlide();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNextSlide, goToPrevSlide]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting && !hasStartedAutoSlide) {
          setHasStartedAutoSlide(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sliderRef.current) {
      observer.observe(sliderRef.current);
    }

    return () => observer.disconnect();
  }, [hasStartedAutoSlide]);

  useEffect(() => {
    if (hasStartedAutoSlide && isVisible) {
      resetAutoSlide();
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [hasStartedAutoSlide, isVisible, resetAutoSlide]);

  const currentItem = slidersData[currentSlide];

  return (
    <div
      ref={sliderRef}
      className={`${styles.sliderContainer} ${className || ""}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.sliderImageWrapper}>
        <ImageResponsiveAssets
          name={currentItem.image}
          filePath={`/images/onboarding-journey`}
          alt={`onboard-${currentSlide}`}
          fill
          priority={currentSlide === 0}
          className={styles.sliderImage}
        />
        
        {isVisible && currentSlide < slidersData.length - 1 && (
          <div style={{ display: 'none' }}>
            <ImageResponsiveAssets
              name={slidersData[currentSlide + 1].image}
              filePath={`/images/onboarding-journey`}
              alt={`preload-${currentSlide + 1}`}
              width={50}
              height={50}
              priority={false}
            />
          </div>
        )}
      </div>
      
      <div className={styles.sliderContentWrapper}>
        <div className={styles.sliderContent}>
          <Text level='h1' className={styles.sliderCotentText}>
            {currentItem.heading}
          </Text>
          <Text className={styles.sliderContentdesc}>
            {currentItem.description}
          </Text>
        </div>
        
        <div className={styles.dots}>
          {slidersData.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`${styles.dot} ${
                currentSlide === index ? styles.activeDot : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <Button
          className={styles.lastButton}
          loginlink={true}
          onButtonClick={() => {
            if (typeof window !== "undefined") {
              sessionStorage.setItem("loginType", "Direct");
              localStorage.setItem(
                "MMV",
                JSON.stringify({
                  make: "",
                  model: "",
                  variant: "",
                  color: "",
                })
              );
            }
          }}
          segmentEventProps={{
            eventName: "action",
            pageType: PAGE_TYPES.HOME_PAGE,
            eventProperties: {
              action_type: "tap_btn",
              action_name: "tap_btn_login",
              city_name: cityName,
            },
          }}
        >
          Create your FREE account
        </Button>
      </div>
    </div>
  );
});

MyProfileHeroSlider.displayName = 'MyProfileHeroSlider';

export default MyProfileHeroSlider;
